import React, { useEffect, useRef, useState } from 'react'
import './PullToRefresh.scss'

interface PullToRefreshProps {
  /**
   * Fired when the user releases a sufficient pull. The indicator stays visible until
   * `refreshing` has gone true and back to false (with a safety timeout in between).
   */
  onRefresh: () => void
  /** The in-progress flag of whatever onRefresh triggers (e.g. the list's loading state). */
  refreshing: boolean
  children: React.ReactNode
}

// Release at or beyond this pull distance triggers a refresh.
const PULL_THRESHOLD_PX = 70
const MAX_PULL_PX = 110
// Where the content rests while the refresh runs.
const REFRESH_HOLD_PX = 56
// The content follows the finger at half speed, like the native control.
const RESISTANCE = 0.5
// Collapse even if the refresh never reports progress (e.g. the action was a no-op).
const SAFETY_TIMEOUT_MS = 5000
// Keep the spinner visible at least this long — a fast refetch otherwise collapses it in a
// blink, which reads as "nothing happened".
const MIN_SPIN_MS = 700
// Indicator strip height (see .pull-to-refresh__indicator).
const INDICATOR_PX = 44

// On mobile widths .layout__content is normally the scroll container, but in the short-viewport
// reflow mode the window scrolls instead — consider both (same convention as scroll.ts).
const scrollOffset = () => {
  const content = document.getElementsByClassName('layout__content')[0]
  return Math.max(window.scrollY, content ? content.scrollTop : 0)
}

/**
 * Touch-only pull-to-refresh for views living in the page scroll container. Wrap the view's
 * content; pulling down while scrolled to the top reveals a spinner and fires `onRefresh` on
 * release. Desktop is unaffected (mouse interactions never trigger touch events).
 *
 * Built for 60fps on the iOS webview: the drag updates only compositor-friendly transforms
 * (never height — that reflows the page per frame), writes styles straight to the DOM instead
 * of going through React state, and claims the gesture with preventDefault so the native
 * rubber-band scroll can't run underneath and double the motion.
 */
export default function PullToRefresh({ onRefresh, refreshing, children }: PullToRefreshProps) {
  // Spinner animation class + collapse watcher — the only piece of React state.
  const [active, setActive] = useState(false)

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const indicatorRef = useRef<HTMLDivElement | null>(null)
  const spinnerRef = useRef<HTMLDivElement | null>(null)

  const gestureRef = useRef({ startY: null as number | null, claimed: false, pull: 0, active: false })
  const activatedAtRef = useRef(0)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return

    const applyPull = (px: number, animate: boolean) => {
      const content = contentRef.current
      const indicator = indicatorRef.current
      const spinner = spinnerRef.current
      if (!content || !indicator || !spinner) return
      const transition = animate ? 'transform 200ms ease-out' : 'none'
      content.style.transition = transition
      indicator.style.transition = animate ? `${transition}, opacity 200ms ease-out` : 'none'
      content.style.transform = px > 0 ? `translate3d(0, ${px}px, 0)` : ''
      // The indicator strip slides in from above so its bottom edge tracks the content edge.
      indicator.style.transform = `translate3d(0, ${px - INDICATOR_PX}px, 0)`
      indicator.style.opacity = String(Math.min(1, px / PULL_THRESHOLD_PX))
      // While dragging the spinner rotates with the pull; while refreshing the CSS animation owns it.
      spinner.style.transform = gestureRef.current.active ? '' : `rotate(${px * 2.5}deg)`
    }

    const settle = (px: number) => {
      gestureRef.current.pull = px
      applyPull(px, true)
    }

    const onTouchStart = (event: TouchEvent) => {
      const gesture = gestureRef.current
      if (gesture.active || scrollOffset() > 0) {
        gesture.startY = null
        return
      }
      gesture.startY = event.touches[0].clientY
      gesture.claimed = false
    }

    const onTouchMove = (event: TouchEvent) => {
      const gesture = gestureRef.current
      if (gesture.startY === null || gesture.active) return
      const delta = event.touches[0].clientY - gesture.startY

      if (!gesture.claimed) {
        // First decisive move: downward at the top claims the gesture from the native scroll;
        // upward hands it over for good (the scroll owns the rest of the gesture).
        if (delta <= 0 || scrollOffset() > 0) {
          gesture.startY = null
          return
        }
        gesture.claimed = true
      }

      // Ours now — keep the webview's rubber-band from running underneath.
      if (event.cancelable) event.preventDefault()

      const pull = Math.max(0, Math.min(delta * RESISTANCE, MAX_PULL_PX))
      gesture.pull = pull
      applyPull(pull, false)
    }

    const onTouchEnd = () => {
      const gesture = gestureRef.current
      if (gesture.startY === null || gesture.active) {
        gesture.startY = null
        return
      }
      gesture.startY = null
      if (!gesture.claimed) return
      if (gesture.pull >= PULL_THRESHOLD_PX) {
        gesture.active = true
        activatedAtRef.current = Date.now()
        setActive(true)
        settle(REFRESH_HOLD_PX)
        onRefreshRef.current()
      } else {
        settle(0)
      }
    }

    // touchmove must be non-passive: claiming the pull requires preventDefault.
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchEnd)
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [])

  // Collapse once the triggered refresh has STARTED and finished — collapsing on the first
  // falsy `refreshing` would race the redux round-trip and snap shut immediately.
  const sawRefreshingRef = useRef(false)
  useEffect(() => {
    const finish = () => {
      gestureRef.current.active = false
      gestureRef.current.pull = 0
      setActive(false)
      const content = contentRef.current
      const indicator = indicatorRef.current
      if (content && indicator) {
        content.style.transition = 'transform 200ms ease-out'
        indicator.style.transition = 'transform 200ms ease-out, opacity 200ms ease-out'
        content.style.transform = ''
        indicator.style.transform = `translate3d(0, ${-INDICATOR_PX}px, 0)`
        indicator.style.opacity = '0'
      }
    }
    if (!active) {
      sawRefreshingRef.current = false
      return
    }
    if (refreshing) {
      sawRefreshingRef.current = true
      return
    }
    if (sawRefreshingRef.current) {
      // Done — but hold the spinner long enough to register.
      const remaining = Math.max(0, MIN_SPIN_MS - (Date.now() - activatedAtRef.current))
      const timeout = setTimeout(finish, remaining)
      return () => clearTimeout(timeout)
    }
    // Safety net: never leave the spinner stuck if the refresh no-ops.
    const timeout = setTimeout(finish, SAFETY_TIMEOUT_MS)
    return () => clearTimeout(timeout)
  }, [active, refreshing])

  return (
    <div ref={wrapperRef} className="pull-to-refresh">
      <div ref={indicatorRef} className="pull-to-refresh__indicator" aria-hidden="true">
        <div
          ref={spinnerRef}
          className={`pull-to-refresh__spinner${active ? ' pull-to-refresh__spinner--spin' : ''}`}
        />
      </div>
      <div ref={contentRef} className="pull-to-refresh__content">
        {children}
      </div>
    </div>
  )
}
